import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to detect if the application is running in standalone mode (installed as PWA)
 * Works on both modern browsers (using matchMedia) and iOS (using navigator.standalone)
 * @returns {boolean} True if the app is running in standalone mode
 */
export const useIsPWA = (): boolean => {
  const [isStandalone, setIsStandalone] = useState<boolean>(false);

  const checkStandalone = useCallback(() => {
    // Modern browsers
    const mediaQueryList = window.matchMedia('(display-mode: standalone)');
    const isDisplayModeStandalone = mediaQueryList.matches;

    // iOS Safari
    const isIOSStandalone =
      'standalone' in navigator &&
      // @ts-ignore - Safari specific property
      navigator.standalone === true;

    setIsStandalone(isDisplayModeStandalone || isIOSStandalone);
  }, []);

  useEffect(() => {
    // Initial check
    checkStandalone();

    // Listen for changes (when app is installed or uninstalled)
    const mediaQueryList = window.matchMedia('(display-mode: standalone)');

    // Modern browsers support addEventListener
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', checkStandalone);
    } else {
      // Fallback for older browsers and iOS
      window.addEventListener('resize', checkStandalone);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', checkStandalone);
      } else {
        window.removeEventListener('resize', checkStandalone);
      }
    };
  }, [checkStandalone]);

  return isStandalone;
};
